/**
 * QuoteSubmissionForm Component
 * Form for contractors to submit quotes for project invitations
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface QuoteSubmissionFormProps {
	onSubmit: (data: {
		quoteAmount: number;
		quoteDetails?: string;
	}) => Promise<void>;
	onCancel: () => void;
	isLoading?: boolean;
}

export function QuoteSubmissionForm({
	onSubmit,
	onCancel,
	isLoading = false,
}: QuoteSubmissionFormProps) {
	const [quoteAmount, setQuoteAmount] = useState("");
	const [quoteDetails, setQuoteDetails] = useState("");
	const [error, setError] = useState("");

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError("");

		const amount = parseInt(quoteAmount, 10);
		if (!quoteAmount || Number.isNaN(amount) || amount <= 0) {
			setError("Please enter a valid quote amount");
			return;
		}

		try {
			await onSubmit({
				quoteAmount: amount,
				quoteDetails: quoteDetails.trim() || undefined,
			});
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to submit quote");
		}
	};

	return (
		<form onSubmit={handleSubmit} className="space-y-4">
			<div>
				<CurrencyInput
					id="quote-amount"
					label="Quote Amount"
					value={quoteAmount}
					onChange={setQuoteAmount}
					error={error}
					placeholder="Enter amount"
				/>
			</div>

			<div className="space-y-1">
				<Label htmlFor="quote-details">Details (optional)</Label>
				<Textarea
					id="quote-details"
					value={quoteDetails}
					onChange={(e) => setQuoteDetails(e.target.value)}
					placeholder="Describe scope of work, materials, timeline..."
					rows={4}
				/>
			</div>

			<div className="flex gap-3">
				<Button type="submit" variant="primary" disabled={isLoading}>
					{isLoading ? "Submitting..." : "Submit Quote"}
				</Button>
				<Button
					type="button"
					variant="outline"
					onClick={onCancel}
					disabled={isLoading}
				>
					Cancel
				</Button>
			</div>
		</form>
	);
}
